/** Shared billing types and helpers — safe for client and server imports */

import type { I18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { wholeCredits, wholeDollars } from "@shared/credits";
import type { Currency } from "@/lib/currency";
import type { QuotaMode } from "@/lib/admin/org-agreement-shared";

/** Why the chat's paywall is up — the reasons the runtime sends in a
 *  `billing_gate` event (its `PaywallReason`, apps/runtime/src/billing.ts).
 *  Each has a way back for the user to buy. */
export type BillingReason = "trial_budget_exhausted" | "trial_window_elapsed" | "trial_ended_early" | "trial_not_included" | "subscription_canceled" | "subscription_past_due";

export interface BillingStatus {
  status: "trialing" | "active" | "past_due" | "canceled" | "unpaid";
  exempt: boolean;
  trialEndsAt: string | null;
  /** The trial was ended by the runtime's trial-abuse sweep
   *  (subscriptions.trial_denied_at, V478) and has not been re-granted since.
   *  Absent means no. Reaches the browser, so it says that, never why. */
  trialEndedEarly?: boolean;
  currentPeriodEnd: string | null;
  cancelAt: string | null;
  cancelAtPeriodEnd: boolean;
  /** The currency the subscription bills in, when it is one we sell and the
   *  webhook has recorded it (V475). A tier change is charged in it, so prices
   *  quoted to a subscriber use it rather than their location. */
  currency?: Currency | null;
  /** Set when the organization is billed under an agreement with Squidler
   *  (V345): a monthly allowance instead of a pool. While set, `status`, the
   *  trial and the pool do not decide access — `agreement.paused` does. */
  agreement: AllowanceStatus | null;
}

/** An organization's monthly allowance as its members may see it: shares and
 *  states, never money (`my_org_allowance`). */
export interface AllowanceStatus {
  whenExhausted: "stop" | "continue";
  /** refill: a quota that refills every night, so a pause ends tonight (V425).
   *  upfront: the month's allowance at once, so a pause waits for the 1st. */
  quotaMode: QuotaMode;
  /** null when the agreement includes no allowance (billed by usage). */
  allowanceUsedPercent: number | null;
  hasCap: boolean;
  /** Fill-up credit as a share of the monthly allowance; null without one. */
  creditPercent: number | null;
  paused: boolean;
  fillUpsBy: "admins" | "members";
  /** May the VIEWER arrange more themselves — an admin of the organization, or
   *  any member where the organization allows it? It decides whether a surface
   *  says "you can" or "an admin can". */
  viewerMayArrange: boolean;
  periodEnd: string;
}

/**
 * Check if a billing status represents an allowed (non-blocked) state. Mirrors
 * the server-side checkBillingGate (apps/runtime/src/billing.ts): access =
 * "has spendable quota from any source". A trial ends when EITHER its window
 * elapses OR its grant is spent — but purchased top-up credit (pay-as-you-go)
 * buys access in EVERY state, so the preflight must be quota-aware in both
 * directions: a spent-out trialing user with no credit must not pass the
 * hire / onboarding gates (orphaned resources), and a topped-up user must not
 * be blocked here while the runtime would happily serve them.
 */
export const isBillingAllowed = (billing: BillingStatus, budget: BudgetStatus): boolean => {
  if (billing.exempt) return true;
  // An account we fund (staff dogfooding, alumni courtesy) is governed by its
  // pool, not by a subscription lifecycle it never had — the same rule as the
  // runtime's checkBillingGate, and for the same incident: these rows keep
  // whatever `status` they had when the plan was assigned, so reading it
  // refuses them the moment an exemption is revoked.
  if (budget.planBillingClass === "internal") return true;
  // An agreement is paid on its own terms; only running out of room closes the gate.
  if (billing.agreement) return !billing.agreement.paused;
  if (billing.status === "active") return true;
  // Purchased credit is state-independent: it unblocks an expired trial, a
  // past_due sub, even a canceled one ("never say no to a payer").
  if (budget.purchasedCredit > 0) return true;
  if (billing.status === "trialing") {
    // Trial not started yet (lazy start on first message) — budget is freshly
    // seeded, so allow. Once started, block on a spent budget or a closed window.
    if (!billing.trialEndsAt) return true;
    if (budget.remaining <= 0) return false;
    return new Date(billing.trialEndsAt) > new Date();
  }
  return false;
};

/**
 * Which organization a link to /billing is about.
 *
 * A person can belong to several organizations, each with its own pool, and a
 * bare `/billing` can only guess which one they meant (see billing-account.ts).
 * A link sent from somewhere that knows the agent says so: `agent` is an instance's URL slug,
 * `org` an organization id. The page checks membership either way, so neither
 * grants anything; an unknown value falls back to the default.
 */
export interface BillingScope {
  readonly org?: string | null;
  readonly agent?: string | null;
}

/** `/billing`, scoped to an organization or an agent's organization when known.
 *  `org` wins over `agent`: it is the more specific answer. */
export const billingHref = (scope?: BillingScope | null, extra: Readonly<Record<string, string>> = {}): string => {
  const params = new URLSearchParams({
    ...(scope?.org ? { org: scope.org } : scope?.agent ? { agent: scope.agent } : {}),
    ...extra,
  });
  const query = params.toString();
  return query ? `/billing?${query}` : "/billing";
};

/** Cents → "$200" / "$8.50". Whole dollars drop the decimals. Shared so the
 *  marketing pages, profile menu, and billing page format budgets identically
 *  (and never round a non-round value up into an over-promise). */
/** The onboarding 402: says WHAT is wrong and WHAT to do about it. The client
 *  pairs it with a CTA linking to /billing (see the billingBlocked banner in
 *  onboarding-flow.tsx) — mirror of the chat budget-banner pattern. */
/** What an onboarding 402 says: the reason, and the organization whose pool it
 *  checked — the one the banner's billing link must open on, which for a member
 *  of a company account is the company's rather than their own default. */
export interface OnboardingBlocked {
  readonly message: string;
  readonly orgId: string | null;
}

export const onboardingBlockedFrom = (body: unknown, message: string): OnboardingBlocked => {
  // `message` is the caller's localized text (ONBOARDING_BLOCKED): the route's
  // `error` is English, for logs, and is deliberately not shown.
  const record = typeof body === "object" && body !== null ? body : {};
  const orgId = "billingOrgId" in record ? record.billingOrgId : undefined;
  return { message, orgId: typeof orgId === "string" ? orgId : null };
};

export const ONBOARDING_BLOCKED_MESSAGE =
  "There are no credits available to set up a new agent right now. Subscribing to a plan — or a one-time top-up — gets you going right away.";

/** The same refusal in the viewer's language. Clients render this on a 402 —
 *  never the route's `error`, which is the English text above, for logs. */
export const ONBOARDING_BLOCKED = msg`There are no credits available to set up a new agent right now. Subscribing to a plan — or a one-time top-up — gets you going right away.`;

const CREDITS = msg`{n, plural, one {# credit} other {# credits}}`;

/** A stored amount as the customer's unit: "2,800 credits" (whole credits,
 *  floored — never a credit the pool does not hold; grouped for the locale).
 *  The one formatter for every customer surface, so a balance reads the same
 *  on the billing page, in the profile menu and on the organization page.
 *  For a sentence that carries its own noun ("# top-up credits left") use
 *  `wholeCredits` with `<Plural>` instead. */
export const formatCredits = (cents: number, i18n: I18n): string =>
  i18n._({ ...CREDITS, values: { n: wholeCredits(cents) } });

/** Trial spend-window as a phrase that reads after "your first …":
 *  14 → "two weeks", 7 → "week", other multiples of 7 → "N weeks",
 *  else → "N days". The window is admin-editable (plans.trial_window_days),
 *  so marketing copy derives the phrase from the DB value — never hardcode
 *  "two weeks" in JSX. Localized via the caller's i18n: the phrase gets
 *  interpolated into already-translated sentences ("Gratis för dina första
 *  {0}"), so an English phrase here would leak into every locale. */
export const formatTrialWindow = (days: number, i18n: I18n): string => {
  if (days === 14) return i18n._(msg`two weeks`);
  if (days === 7) return i18n._(msg`week`);
  if (days === 1) return i18n._(msg`day`);
  if (days % 7 === 0) {
    const weeks = days / 7;
    return i18n._(msg`${weeks} weeks`);
  }
  return i18n._(msg`${days} days`);
};

/** Preformatted free-trial offer for marketing surfaces (landing heroes,
 *  pricing card): worth like "$200", window like "two weeks". The grant is
 *  phrased as money on purpose: to someone who has not used the product yet
 *  "20,000 credits" means nothing, while "$200" does — the credits are what
 *  they will reason about later. Each sentence supplies its own "credits worth
 *  {worth}", so the phrase translates as part of a whole sentence. */
export interface TrialOffer {
  /** What the trial grant is worth, as money: "$200". */
  worth: string;
  window: string;
}

/** What a stored amount is worth in USD at the launch rate (1 credit = 1
 *  cent), as whole dollars in the reader's locale: "$200", "200 US$". */
export const formatCreditsWorth = (cents: number, i18n: I18n): string =>
  new Intl.NumberFormat(i18n.locale, { style: "currency", currency: "USD", maximumFractionDigits: 0 })
    .format(wholeDollars(cents));

const CREDITS_ABOUT = msg`{credits} (about {worth})`;

/** A grant as what it is — credits — with its money value beside it:
 *  "20,000 credits (about $200)". For the referral program, where the trial
 *  and the reward are credits but a dollar figure is what makes them mean
 *  something (decided 2026-10-27). Sentences use it as one noun phrase. */
export const formatCreditsAbout = (cents: number, i18n: I18n): string =>
  i18n._({ ...CREDITS_ABOUT, values: { credits: formatCredits(cents, i18n), worth: formatCreditsWorth(cents, i18n) } });

/** Derive the marketing trial offer from the trial plan row. The 14-day
 *  fallback mirrors TRIAL_WINDOW_FALLBACK_DAYS (apps/runtime/src/config.ts) —
 *  billing.startTrial applies it when plans.trial_window_days is NULL, so the
 *  advertised window always matches the one users actually get. */
export const trialOffer = (
  plan: { budget: number; trialWindowDays: number | null } | null,
  i18n: I18n,
): TrialOffer | null =>
  plan
    ? { worth: formatCreditsWorth(plan.budget, i18n), window: formatTrialWindow(plan.trialWindowDays ?? 14, i18n) }
    : null;

/** Days left in trial, or null if not trialing / not started */
export const trialDaysLeft = (billing: BillingStatus): number | null => {
  if (billing.status !== "trialing" || !billing.trialEndsAt) return null;
  return Math.max(0, Math.ceil((new Date(billing.trialEndsAt).getTime() - Date.now()) / 86400000));
};

/** Snapshot of the billing owner's pools. Server fetches this on page load;
 *  the WS `budget_update` event keeps the client copy fresh. Every amount is
 *  in stored cents, which are credits at 1 credit = 1 cent (`@shared/credits`);
 *  render them with `formatCredits`, never as money. */
export interface BudgetStatus {
  /** The daily credits remaining in the plan pool. */
  remaining: number;
  /** Top-up credits (V200) — non-expiring, spend in every subscription state
   *  after the plan pool. */
  purchasedCredit: number;
  /** The plan pool's ceiling (max value of `remaining` after a refill). */
  planBudget: number;
  /** Plan name for display ("default", future tiers, etc.) — the EFFECTIVE
   *  pool, which an admin override may have moved off what was bought. */
  planName: string;
  /** `purchased_plan_id`'s row name — the tier Stripe actually bills for, or
   *  null for an account that has never bought one.
   *
   *  Distinct from `planName` precisely when a human has overridden the account
   *  (`plan_overridden_at`), and that gap is load-bearing for the upgrade
   *  chooser: keyed on the effective plan, a sponsored 'champion' resolves to
   *  no tier at all and is offered nothing, leaving the Stripe portal as their
   *  only way to change tier. That is how the 2026-09-18 product crossover
   *  started. The server half (lib/plan-change.ts) has always derived the
   *  current tier from Stripe's price for the same reason; this is the client
   *  half of the same question. */
  purchasedPlanName: string | null;
  /** plans.billing_class (V287). 'internal' means WE fund this account, which
   *  isBillingAllowed reads the same way the runtime gate does. */
  planBillingClass: string;
}
